import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AiOutlineArrowLeft, AiFillGithub } from "react-icons/ai";
import { BsPlay, BsGlobe } from "react-icons/bs";
import Particle from "../Particle";
import { getProjectById } from "../../data/projectsData";
import { getRelatedProjects } from "../../utils/projectUtils";

/* ─── Lightbox ─────────────────────────────────────────────────── */
function Lightbox({ src, alt, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.92)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px", cursor: "zoom-out",
      }}
    >
      <img
        src={src} alt={alt}
        style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: "12px", objectFit: "contain" }}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        style={{
          position: "fixed", top: "20px", right: "28px",
          background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "50%", width: "44px", height: "44px",
          color: "#fff", fontSize: "1.4rem", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >×</button>
    </div>
  );
}

/* ─── Pill / badge ──────────────────────────────────────────────── */
function Pill({ children, accent }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "5px 14px",
      borderRadius: "50px",
      fontSize: "0.8rem",
      fontWeight: 500,
      letterSpacing: "0.04em",
      background: accent
        ? "linear-gradient(135deg,rgba(102,126,234,0.18),rgba(118,75,162,0.18))"
        : "rgba(255,255,255,0.06)",
      border: `1px solid ${accent ? "rgba(102,126,234,0.35)" : "rgba(255,255,255,0.12)"}`,
      color: accent ? "#c4b5fd" : "rgba(255,255,255,0.75)",
    }}>
      {children}
    </span>
  );
}

/* ─── Section wrapper ───────────────────────────────────────────── */
function Section({ children, style }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.035)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "20px",
      padding: "40px 36px",
      marginBottom: "32px",
      ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: "1.05rem",
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: "#7c83ff",
      marginBottom: "24px",
    }}>
      {children}
    </h2>
  );
}

/* ─── Main component ────────────────────────────────────────────── */
function ProjectDetails() {
  const { projectId } = useParams();
  const project = getProjectById(projectId);
  const [lightbox, setLightbox] = useState(null);

  if (!project) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#07090f" }}>
        <Particle />
        <div style={{ textAlign: "center" }}>
          <h1 style={{ color: "#fff", fontSize: "2rem", fontWeight: 300, marginBottom: "16px" }}>Project not found</h1>
          <Link to="/project" style={{ color: "#7c83ff", textDecoration: "none" }}>← Back to Projects</Link>
        </div>
      </div>
    );
  }

  const allScreenshots = (project.screenshotSections || []).flatMap(s => s.screenshots);

  return (
    <div style={{ background: "#07090f", minHeight: "100vh", color: "#fff" }}>
      <Particle />
      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px 100px" }}>

        {/* ── Back ── */}
        <div style={{ paddingTop: "40px", marginBottom: "36px" }}>
          <Link to="/project" style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            color: "rgba(255,255,255,0.55)", textDecoration: "none",
            fontSize: "0.9rem", transition: "color .2s",
          }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
          >
            <AiOutlineArrowLeft /> Back to Projects
          </Link>
        </div>

        {/* ── Hero ── */}
        <div style={{
          position: "relative", borderRadius: "24px", overflow: "hidden",
          background: "linear-gradient(135deg,rgba(102,126,234,0.12) 0%,rgba(118,75,162,0.08) 100%)",
          border: "1px solid rgba(102,126,234,0.2)",
          padding: "56px 48px 48px",
          marginBottom: "32px",
        }}>
          {/* glow */}
          <div style={{
            position: "absolute", top: "-80px", right: "-80px",
            width: "360px", height: "360px",
            background: "radial-gradient(circle,rgba(102,126,234,0.15),transparent 70%)",
            pointerEvents: "none",
          }} />

          <span style={{
            display: "inline-block", marginBottom: "16px",
            padding: "4px 14px", borderRadius: "50px",
            fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
            background: "rgba(124,131,255,0.15)", border: "1px solid rgba(124,131,255,0.3)",
            color: "#a5b4fc",
          }}>
            {project.category}
          </span>

          <h1 style={{
            fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 700,
            lineHeight: 1.15, marginBottom: "20px",
            background: "linear-gradient(135deg,#fff 30%,#c4b5fd 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            {project.title}
          </h1>

          <p style={{
            color: "rgba(255,255,255,0.7)", fontSize: "1.05rem",
            lineHeight: 1.8, maxWidth: "780px", marginBottom: "36px",
          }}>
            {project.description}
          </p>

          {/* Action buttons */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            {project.githubLink && (
              <a href={project.githubLink} target="_blank" rel="noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "11px 24px", borderRadius: "50px",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                color: "#fff", fontWeight: 600, fontSize: "0.9rem",
                textDecoration: "none", boxShadow: "0 4px 20px rgba(102,126,234,0.35)",
                transition: "transform .2s,box-shadow .2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(102,126,234,0.45)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(102,126,234,0.35)"; }}
              >
                <AiFillGithub size={18} /> GitHub
              </a>
            )}
            {project.demoVideo && (
              <a href={project.demoVideo} target="_blank" rel="noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "11px 24px", borderRadius: "50px",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "#fff", fontWeight: 600, fontSize: "0.9rem",
                textDecoration: "none", transition: "background .2s,transform .2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.transform = ""; }}
              >
                {project.demoVideo.includes("apps.apple.com") ? (
                  <>
                    <img src="/logos/appstore.png" alt="App Store" style={{ height: "16px", width: "auto" }} />
                    App Store
                  </>
                ) : project.demoVideo.includes("play.google.com") ? (
                  <>
                    <img src="/logos/playstore.png" alt="Play Store" style={{ height: "16px", width: "auto" }} />
                    Play Store
                  </>
                ) : (
                  <>
                    <BsPlay size={18} /> Live Demo
                  </>
                )}
              </a>
            )}
            {project.websiteLink && (
              <a href={project.websiteLink} target="_blank" rel="noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "11px 24px", borderRadius: "50px",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "#fff", fontWeight: 600, fontSize: "0.9rem",
                textDecoration: "none", transition: "background .2s,transform .2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.transform = ""; }}
              >
                <BsGlobe size={16} /> Website
              </a>
            )}
          </div>
        </div>

        {/* ── Stats strip ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Object.keys(project.projectStats).length}, 1fr)`,
          gap: "2px",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
          overflow: "hidden",
          marginBottom: "32px",
        }}>
          {Object.entries(project.projectStats).map(([key, value]) => (
            <div key={key} style={{ padding: "22px 20px", textAlign: "center", background: "#07090f" }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7c83ff", marginBottom: "6px" }}>
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </div>
              <div style={{ fontSize: "0.95rem", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Tech Stack ── */}
        <Section>
          <SectionTitle>Tech Stack</SectionTitle>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {project.techStack.map((t) => <Pill key={t} accent>{t}</Pill>)}
          </div>
        </Section>

        {/* ── Screenshots ── */}
        {allScreenshots.length > 0 && (
          <Section>
            <SectionTitle>Screenshots</SectionTitle>
            {project.screenshotSections.map((section, si) => (
              <div key={si} style={{ marginBottom: si < project.screenshotSections.length - 1 ? "40px" : 0 }}>
                {project.screenshotSections.length > 1 && (
                  <p style={{ fontSize: "0.85rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "16px" }}>
                    {section.title}
                  </p>
                )}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
                  gap: "16px",
                  justifyItems: "center",
                }}>
                  {section.screenshots.map((shot, i) => {
                    const imgSrc = shot.src.startsWith('/') ? `${process.env.PUBLIC_URL}${shot.src}` : shot.src;
                    return (
                    <div
                      key={i}
                      onClick={() => setLightbox({ src: imgSrc, alt: shot.title })}
                      style={{
                        borderRadius: "14px", overflow: "hidden",
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        cursor: "zoom-in", transition: "transform .25s,box-shadow .25s",
                        width: "100%",
                        maxWidth: "240px",
                        gridColumn: "auto",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.4)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
                      onLoad={(e) => {
                        const card = e.currentTarget;
                        const img = card.querySelector('img');
                        if (img && img.complete) {
                          if (img.naturalWidth > img.naturalHeight * 1.2) {
                            // Landscape - wider than portrait
                            card.style.maxWidth = "480px";
                            card.style.gridColumn = "span 2";
                          }
                        }
                      }}
                    >
                      <img
                        src={imgSrc} alt={shot.title}
                        style={{ width: "100%", height: "auto", display: "block", maxHeight: "550px" }}
                        onLoad={(e) => {
                          const img = e.target;
                          const card = img.closest('div');
                          if (img.naturalWidth > img.naturalHeight * 1.2) {
                            // Landscape - wider than portrait
                            card.style.maxWidth = "480px";
                            card.style.gridColumn = "span 2";
                          }
                        }}
                      />
                      <div style={{ padding: "12px 12px" }}>
                        <p style={{ margin: 0, fontSize: "0.82rem", fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>{shot.title}</p>
                        {shot.description && (
                          <p style={{ margin: "3px 0 0", fontSize: "0.73rem", color: "rgba(255,255,255,0.45)" }}>{shot.description}</p>
                        )}
                      </div>
                    </div>
                  ); })}
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* ── Features ── */}
        <Section>
          <SectionTitle>Features</SectionTitle>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
            gap: "20px",
          }}>
            {project.features.map((f, i) => (
              <div key={i} style={{
                padding: "24px",
                background: "rgba(255,255,255,0.035)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "16px",
                transition: "border-color .2s,background .2s",
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(102,126,234,0.35)"; e.currentTarget.style.background = "rgba(102,126,234,0.06)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.background = "rgba(255,255,255,0.035)"; }}
              >
                <div style={{ fontSize: "1.6rem", marginBottom: "12px" }}>{f.icon}</div>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#fff", marginBottom: "14px" }}>{f.title}</h3>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {f.details.map((d, di) => (
                    <li key={di} style={{
                      display: "flex", alignItems: "flex-start", gap: "10px",
                      marginBottom: "8px", fontSize: "0.85rem", color: "rgba(255,255,255,0.65)",
                    }}>
                      <span style={{ color: "#7c83ff", marginTop: "3px", flexShrink: 0 }}>›</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Highlights ── */}
        <Section>
          <SectionTitle>Highlights</SectionTitle>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
            gap: "12px",
          }}>
            {project.keyHighlights.map((h, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: "14px",
                padding: "16px 20px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "12px",
              }}>
                <div style={{
                  width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.75rem", fontWeight: 700, color: "#fff",
                }}>✓</div>
                <span style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.8)" }}>{h}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Related Projects ── */}
        {getRelatedProjects(projectId, 3).length > 0 && (
          <div>
            <h2 style={{
              fontSize: "1.05rem", fontWeight: 600, letterSpacing: "0.12em",
              textTransform: "uppercase", color: "#7c83ff", marginBottom: "20px",
            }}>Related Projects</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "16px" }}>
              {getRelatedProjects(projectId, 3).map((rp) => (
                <Link key={rp.id} to={`/project/${rp.id}`} style={{ textDecoration: "none" }}>
                  <div style={{
                    padding: "24px",
                    background: "rgba(255,255,255,0.035)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "16px",
                    transition: "border-color .2s,transform .2s",
                    height: "100%",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(102,126,234,0.35)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.transform = ""; }}
                  >
                    <span style={{ fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7c83ff" }}>{rp.category}</span>
                    <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#fff", margin: "10px 0 10px", lineHeight: 1.4 }}>{rp.title}</h3>
                    <p style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.6, margin: "0 0 14px" }}>
                      {(rp.shortDescription || rp.description || "").substring(0, 100)}…
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {rp.techStack.slice(0, 3).map(t => <Pill key={t}>{t}</Pill>)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default ProjectDetails;
